import { useState, type FormEvent } from "react";
import { ArrowRight, KeyRound, LockKeyhole, ShieldCheck, Smartphone } from "lucide-react";
import { ApiClientError, authApi } from "../lib/api";
import type { SessionView } from "@control-plane/contracts";
import { PLATFORM_ADMIN_PASSWORD_MIN_LENGTH } from "@control-plane/contracts";
import { InlineFeedback } from "../components/Feedback";

export function LoginPage({ onLogin }: { onLogin: (session: SessionView) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    if (!email.trim() || !password) {
      setError(!email.trim() && !password ? "请先填写管理员邮箱和密码，再点击安全登录。" : !email.trim() ? "请填写管理员邮箱后再登录。" : "请填写密码后再登录。");
      setBusy(false);
      return;
    }
    try {
      if (needsMfa) {
        onLogin(await authApi.loginWithMfa(email, password, totpCode.trim()));
        return;
      }
      try {
        onLogin(await authApi.login(email, password));
      } catch (failure) {
        if (failure instanceof ApiClientError && failure.code === "MFA_REQUIRED") {
          setNeedsMfa(true);
          setError("该账号已开启双重验证，请继续输入动态码。");
          return;
        }
        throw failure;
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : needsMfa ? "动态码验证失败" : "登录失败");
      if (needsMfa) setTotpCode("");
    } finally { setBusy(false); }
  }

  function backToPassword() { setNeedsMfa(false); setTotpCode(""); setError(""); }

  return <div className="login-page">
    <section className="login-story">
      <div className="brand hero"><span><ShieldCheck /></span><div><strong>平台总管</strong><small>APP FINAL CONTROL PLANE</small></div></div>
      <div>
        <p className="eyebrow">PLATFORM OPERATIONS</p>
        <h1>把每一次跨租户操作<br />放进可验证的安全边界。</h1>
        <p>租户生命周期、功能授权、凭据轮换和受控代管集中治理。这里不承载任何租户业务操作。</p>
      </div>
      <div className="security-points">
        <span><LockKeyhole />独立管理员会话</span>
        <span><KeyRound />凭据永不回显</span>
        <span><ShieldCheck />高风险操作留痕</span>
      </div>
    </section>
    <section className="login-panel">
      <form onSubmit={submit} noValidate={false}>
        <p className="eyebrow">SECURE ACCESS</p>
        <h2>{needsMfa ? "输入动态验证码" : "登录总管后台"}</h2>
        <p className="muted">{needsMfa ? "请打开身份验证器应用，输入当前显示的 6 位动态码。" : "仅限已授权的平台运营与安全人员。"}</p>
        {!needsMfa && <>
          <label>管理员邮箱<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required /></label>
          <label>密码<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" minLength={PLATFORM_ADMIN_PASSWORD_MIN_LENGTH} required /></label>
        </>}
        {needsMfa && <>
          <div className="login-mfa-hint"><Smartphone size={18} aria-hidden /><span>账号 <strong>{email}</strong> 已开启双重验证</span></div>
          <label>6 位动态码
            <input
              className="login-totp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value.replace(/[^0-9]/g, ""))}
              placeholder="例如 482913"
              autoFocus
              required
            />
          </label>
        </>}
        <div aria-live="polite">{error && <InlineFeedback tone={needsMfa ? "warning" : "danger"}>{error}</InlineFeedback>}</div>
        <button className="button primary wide" disabled={busy}>{busy ? "正在验证…" : needsMfa ? "验证并登录" : "安全登录"}<ArrowRight size={17} /></button>
        {needsMfa && <button type="button" className="login-back" onClick={backToPassword}>返回重新输入账号密码</button>}
        <small className="login-hint">连续失败 5 次将锁定账号 15 分钟。</small>
      </form>
    </section>
  </div>;
}
